import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { captureEvent } from "@/lib/analyticsQueue";
import { useCredits } from "@/contexts/CreditsContext";
import { useCreditCosts } from "@/hooks/useCreditCosts";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/lib/toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useGeneratedVideos } from "@/hooks/useGeneratedVideos";
import { useModels } from "@/hooks/useModels";
import { useModelWarning } from "@/hooks/useModelWarning";
import { useVideoGeneration } from "@/hooks/useVideoGeneration";
import { useGenerationRestore } from "@/hooks/useGenerationRestore";
import { useLightboxNavigation } from "@/hooks/useLightboxNavigation";
import { usePersistentPendingBoost } from "@/hooks/usePersistentPendingBoost";
import { captureGenerationError } from "@/lib/sentry";
import {
  clearFlowState,
  useFlowPersistence,
} from "@/hooks/useFlowPersistence";
import { FLOW_KEYS } from "@/config/flowKeys";
import { improvePrompt } from "@/lib/api/improvePrompt";
import { ACCEPTED_IMAGE_TYPES } from "@/config/media";
import { useImageUpload } from "@/hooks/useImageUpload";
import { downloadMedia, isUrl, shareMedia } from "@/lib/mediaUtils";
import { processImageForUpload } from "@/lib/imageProcessing";
import { uploadSingleImageToR2 } from "@/lib/api/uploadImage";
import { ASPECT_RATIOS } from "@/components/flowly/AspectRatioPicker/constants";
import { ANALYTICS_EVENTS, FLOW_IDS } from "@shared/schemas/analytics";

import { videoToDisplayMedia } from "@/lib/types/media";
import type { DisplayMedia } from "@/lib/types/media";

export function useTextToVideo() {
  const location = useLocation();
  const { deductCredits, refreshCredits, handleCreditError } = useCredits();
  const { getVideoCost, getVideoModelCost, costsLoading } = useCreditCosts();
  const { user, profile } = useAuth();
  const { t, language, locale } = useLanguage();
  const { normalizeOrWarn } = useImageUpload();
  const { models: allVideoModels, refetchModels } = useModels("video");

  // All video models support text-to-video (end-frame UI is disabled on this page)
  const availableModels = allVideoModels;

  // --- Refs ---
  const fileInputRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);

  // --- Navigation state ---
  const animateImage = location.state?.animateImage;

  // --- State ---
  const [filePreview, setFilePreview] = useState<string | null>(animateImage || null);

  const [videoPrompt, setVideoPrompt] = useState("");
  const [videoDuration, setVideoDuration] = useState<number>(5);
  const [videoResolution, setVideoResolution] = useState<string>("1080p");
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [aspectRatio, setAspectRatio] = useState("16:9");

  const [isGenerating, setIsGenerating] = useState(false);
  const [isEnhancingVideoPrompt, setIsEnhancingVideoPrompt] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<DisplayMedia | null>(null);

  // --- Model selection (dynamic, default from registry) ---
  const [videoModel, setVideoModel] = useState<string>("");
  const modelWarning = useModelWarning(videoModel, allVideoModels);

  // --- Derived model capabilities ---
  const selectedModelConfig = allVideoModels.find((m) => m.id === videoModel);
  const supportsSound = selectedModelConfig?.capabilities.supportsSound ?? false;
  const alwaysIncludesAudio = selectedModelConfig?.capabilities.alwaysIncludesAudio ?? false;
  const supportsImageInput = selectedModelConfig?.capabilities.supportsImageInput !== false;
  const modelDisplayName = selectedModelConfig?.displayName ?? videoModel;
  const promptPlaceholderKey = selectedModelConfig?.capabilities.promptPlaceholderKey ?? "videoPromptPlaceholder";
  const promptPlaceholder = (t.generate as Record<string, string>)[promptPlaceholderKey] ?? t.generate.videoPromptPlaceholder;

  const availableAspectRatios = useMemo(() => {
    const supported = selectedModelConfig?.capabilities.supportedAspectRatios;
    if (!supported) return ASPECT_RATIOS;
    return ASPECT_RATIOS.filter((r) => supported.includes(r.id));
  }, [selectedModelConfig]);

  const availableDurations = useMemo(() => {
    const labels: Record<number, string> = {
      4: t.generate.fourSeconds,
      5: t.generate.fiveSeconds,
      8: t.generate.eightSeconds,
      10: t.generate.tenSeconds,
      12: t.generate.twelveSeconds,
    };
    const supported = selectedModelConfig?.capabilities.supportedDurations ?? [5, 10];
    return supported.map((d) => ({ value: d, label: labels[d] ?? `${d}s` }));
  }, [selectedModelConfig, t]);

  const availableResolutions = useMemo(() => {
    const supported = selectedModelConfig?.capabilities.supportedResolutions ?? ["720p", "1080p"];
    return supported.map((r) => ({ value: r, label: r }));
  }, [selectedModelConfig]);

  // --- Video generation hooks ---
  const { completedVideos, pendingVideos: dbPendingVideos, completingIds: videoCompletingIds, loading: videosLoading } = useGeneratedVideos("text-to-video");

  // Persistent pending boost — survives page refresh via sessionStorage
  const { pendingCount, increment: incrementPending, decrement: decrementPending } =
    usePersistentPendingBoost({ pipeline: "text-to-video", dbPendingCount: dbPendingVideos.length, isDbLoading: videosLoading });

  // Restore prompt & image from pending DB items on page refresh
  useGenerationRestore({
    pendingItems: dbPendingVideos,
    isLoading: videosLoading,
    currentPrompt: videoPrompt,
    setPrompt: setVideoPrompt,
    currentImage: filePreview,
    setImage: setFilePreview,
    skip: !!animateImage,
  });

  // Pre-map completed videos to DisplayMedia for gallery rendering
  const galleryVideos: DisplayMedia[] = useMemo(
    () => completedVideos.filter((v) => v.video_url).map(videoToDisplayMedia),
    [completedVideos],
  );
  const {
    startGeneration: startVideoGeneration,
    isStarting: isStartingVideo,
  } = useVideoGeneration();

  // --- Effects ---

  // Set default model from available list
  useEffect(() => {
    if (!videoModel && availableModels.length > 0) {
      const defaultModel = availableModels.find((m) => m.isDefault) ?? availableModels[0];
      setVideoModel(defaultModel.id);
    }
  }, [availableModels, videoModel]);

  // --- Analytics: tool page view ---
  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.TOOL_PAGE_VIEW, { flow: FLOW_IDS.TEXT_TO_VIDEO });
  }, []);

  useEffect(() => {
    if (animateImage || location.state?.prompt) {
      if (animateImage) setFilePreview(animateImage);
      if (location.state?.prompt) setVideoPrompt(location.state.prompt);
      window.history.replaceState({}, document.title);
    }
  }, [animateImage, location.state?.prompt]);

  // --- Handlers ---

  const handleFileSelect = async (rawFile: File) => {
    const selectedFile = await normalizeOrWarn(rawFile);
    if (!selectedFile) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(selectedFile.type)) return;
    captureEvent(ANALYTICS_EVENTS.IMAGE_UPLOAD, { flow: FLOW_IDS.TEXT_TO_VIDEO });
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setFilePreview(result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) handleFileSelect(selectedFile);
  };

  const clearFile = () => {
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleVideoModelChange = (modelId: string) => {
    setVideoModel(modelId);
    const newModel = allVideoModels.find((m) => m.id === modelId);
    const supported = newModel?.capabilities.supportedAspectRatios;
    if (supported && !supported.includes(aspectRatio)) {
      setAspectRatio(supported[0] ?? "16:9");
    }
    const supportedDurations = newModel?.capabilities.supportedDurations;
    if (supportedDurations && !supportedDurations.includes(videoDuration)) {
      setVideoDuration(supportedDurations[0]);
    }
    const supportedResolutions = newModel?.capabilities.supportedResolutions;
    if (supportedResolutions && !supportedResolutions.includes(videoResolution)) {
      setVideoResolution(supportedResolutions[0]);
    }
    // Clear uploaded image when switching to a model that doesn't support image input
    if (newModel?.capabilities.supportsImageInput === false && filePreview) {
      clearFile();
    }
  };

  const handleEnhancePrompt = async () => {
    if (!videoPrompt.trim() || isEnhancingVideoPrompt) return;
    captureEvent(ANALYTICS_EVENTS.PROMPT_ENHANCE_CLICK, { flow: FLOW_IDS.TEXT_TO_VIDEO });
    setIsEnhancingVideoPrompt(true);
    try {
      const response = await improvePrompt({ prompt: videoPrompt, mode: "video" });
      if (response.success && response.improvedPrompt) {
        setVideoPrompt(response.improvedPrompt);
        toast.success(t.generate.promptImproved, {
          description: t.generate.promptImprovedDesc,
        });
      } else if (!response.isRateLimited) {
        toast.error(t.generate.enhanceFailed, {
          description: response.error || t.generate.tryAgain,
        });
      }
    } catch (error) {
      console.error("Error improving prompt:", error);
      captureGenerationError(error, {
        flow: FLOW_IDS.TEXT_TO_VIDEO,
        action: "enhance_prompt",
        endpoint: "/api/improve-prompt",
      });
      toast.error(t.common.error, {
        description: t.generate.enhanceError,
      });
    } finally {
      setIsEnhancingVideoPrompt(false);
    }
  };

  const generateVideo = async () => {
    if (!videoPrompt.trim()) return;

    const plan = profile?.subscription_name;
    if (isGenerating && plan !== "pro" && plan !== "creator") {
      toast.info(t.generate.generationInProgress, { description: t.generate.parallelProRequired });
      return;
    }

    const hasOptionalAudio = soundEnabled && !alwaysIncludesAudio;
    const videoCost = getVideoCost(videoDuration, videoResolution, videoModel, hasOptionalAudio);

    if (!deductCredits(videoCost)) return;

    setIsGenerating(true);
    incrementPending(1);

    setTimeout(() => {
      galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    let succeeded = false;
    try {
      // Process image client-side and upload to R2
      let imageUrl: string | undefined;
      if (filePreview) {
        const constraints = selectedModelConfig?.capabilities.imageConstraints;
        const sizeKey = `${aspectRatio}_${videoResolution}`;
        const targetDims = selectedModelConfig?.capabilities.videoSizeMap?.[sizeKey];
        const input = isUrl(filePreview)
          ? await fetch(filePreview, { mode: "cors", cache: "no-store" }).then((r) => r.blob())
          : filePreview;
        const processed = await processImageForUpload(input, {
          ...constraints,
          targetWidth: targetDims?.width,
          targetHeight: targetDims?.height,
        });
        imageUrl = await uploadSingleImageToR2(processed);
      }

      const result = await startVideoGeneration({
        ...(imageUrl ? { imageUrl } : {}),
        prompt: videoPrompt,
        modelId: videoModel,
        duration: videoDuration as 4 | 5 | 8 | 10 | 12,
        resolution: videoResolution as "720p" | "1080p",
        aspectRatio,
        soundEnabled,
        pipeline: "text-to-video",
      });

      if (!result.success) {
        captureGenerationError(result, {
          flow: FLOW_IDS.TEXT_TO_VIDEO,
          action: "generate",
          model: videoModel,
          endpoint: "/api/generate-video",
        });
        refetchModels();
        if (!handleCreditError(result)) {
          toast.error(t.generate.generationFailed, {
            description: result.error || t.generate.generationFailedDesc,
          });
        }
      } else {
        succeeded = true;
      }
    } catch (error) {
      console.error("Error generating video:", error);
      captureGenerationError(error, {
        flow: FLOW_IDS.TEXT_TO_VIDEO,
        action: "generate",
        model: videoModel,
        endpoint: "/api/generate-video",
      });
      if (!handleCreditError(error)) {
        toast.error(t.generate.generationFailed, {
          description: error instanceof Error ? error.message : t.generate.generationFailedDesc,
        });
      }
    } finally {
      setIsGenerating(false);
      if (!succeeded) {
        decrementPending(1);
      }
      await refreshCredits();
      if (succeeded) {
        // Wipe the persisted draft AND reset inputs to defaults — you don't
        // regenerate from the same prompt/image. The result shows in the
        // DB-backed gallery, untouched here. (videoModel is a sticky picker,
        // left as-is.)
        clearFlowState(FLOW_KEYS.TEXT_TO_VIDEO);
        setFilePreview(null);
        setVideoPrompt("");
        setVideoDuration(5);
        setVideoResolution("1080p");
        setSoundEnabled(false);
        setAspectRatio("16:9");
      }
    }
  };
  // Auth gate lives inside GenerateButton, so this is just a rename.
  const handleGenerateVideo = generateVideo;

  // Persist prompt + video settings AND the optional source image across the
  // auth/credit wall. `filePreview` is an image key → its bytes go to
  // IndexedDB, restored as the same data-URL string generate() expects.
  useFlowPersistence({
    key: FLOW_KEYS.TEXT_TO_VIDEO,
    state: { videoPrompt, videoDuration, videoResolution, soundEnabled, aspectRatio, videoModel, filePreview },
    imageKeys: ["filePreview"],
    onRestore: (s) => {
      if (typeof s.videoPrompt === "string") setVideoPrompt(s.videoPrompt);
      if (typeof s.videoDuration === "number") setVideoDuration(s.videoDuration);
      if (typeof s.videoResolution === "string") setVideoResolution(s.videoResolution);
      if (typeof s.soundEnabled === "boolean") setSoundEnabled(s.soundEnabled);
      if (typeof s.aspectRatio === "string") setAspectRatio(s.aspectRatio);
      if (typeof s.videoModel === "string") handleVideoModelChange(s.videoModel);
      if (typeof s.filePreview === "string" && s.filePreview) setFilePreview(s.filePreview);
    },
  });

  // --- Lightbox callbacks ---

  const handleDownload = async () => {
    if (!selectedMedia) return;
    await downloadMedia(selectedMedia.url, "todoai-video", "mp4");
  };

  const handleShare = () => {
    if (!selectedMedia?.id) return;
    void shareMedia({ id: selectedMedia.id, type: selectedMedia.type });
  };

  const handleRegenerate = () => {
    setFilePreview(selectedMedia?.referenceImageUrls[0] || null);
    setVideoPrompt(selectedMedia?.prompt || "");
    setSelectedMedia(null);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
  };

  const handleCloseLightbox = () => setSelectedMedia(null);

  const handleDisabledClick = useCallback(() => {
    document.querySelector('[data-scroll-target="prompt"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // --- Navigation ---

  const { currentMediaIndex, canNavigate, goToNextItem, goToPrevItem } =
    useLightboxNavigation(galleryVideos, selectedMedia, setSelectedMedia);

  // --- Return ---

  return {
    // Refs
    fileInputRef,
    galleryRef,

    // File upload
    filePreview,
    handleInputChange,
    clearFile,
    handleDrop,
    handleDragOver,
    handleDragLeave,

    // Video config
    videoPrompt,
    setVideoPrompt,
    videoDuration,
    setVideoDuration,
    videoResolution,
    setVideoResolution,
    videoModel,
    soundEnabled,
    setSoundEnabled,
    aspectRatio,
    setAspectRatio,
    availableAspectRatios,
    availableDurations,
    availableResolutions,

    // Model capabilities (resolved)
    supportsSound,
    supportsImageInput,
    alwaysIncludesAudio,
    modelDisplayName,
    promptPlaceholder,

    // Generation
    handleGenerateVideo,
    handleEnhancePrompt,
    isEnhancingVideoPrompt,
    isGenerating,
    getVideoCreditCost: getVideoCost,
    getVideoModelCost,
    costsLoading,

    // Gallery
    galleryVideos,
    pendingCount,
    dbPendingVideos,
    videoCompletingIds,
    selectedMedia,
    setSelectedMedia,

    // Lightbox
    handleDownload,
    handleShare,
    handleRegenerate,
    handleCloseLightbox,

    // Navigation
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,

    // Disabled click
    handleDisabledClick,

    // Models
    models: allVideoModels,
    availableModels,
    handleVideoModelChange,
    modelWarning,

    // Derived
    language,
    locale,
    t,
  };
}
